import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { QuickReply, sortQuickReplies } from '../lib/quickReplies';

export interface QuickReplyInput { title: string; body: string; shared: boolean }

/** Mensagens rápidas visíveis ao usuário (as da empresa + as próprias), com criar/editar/apagar. */
export function useQuickReplies(companyId: number | null | undefined, userId: string | null | undefined) {
    const [replies, setReplies] = useState<QuickReply[]>([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        if (!companyId || !supabase) return;
        setLoading(true);
        const { data, error } = await supabase
            .from('quick_replies')
            .select('id, company_id, owner_id, title, body, position')
            .eq('company_id', companyId);
        if (error) console.error('Erro ao carregar mensagens rápidas:', error);
        else setReplies(sortQuickReplies((data || []) as QuickReply[]));
        setLoading(false);
    }, [companyId]);

    useEffect(() => { load(); }, [load]);

    const create = useCallback(async (input: QuickReplyInput) => {
        if (!companyId || !userId || !supabase) return;
        const { error } = await supabase.from('quick_replies').insert({
            company_id: companyId,
            owner_id: input.shared ? null : userId,
            title: input.title.trim(),
            body: input.body.trim(),
            position: replies.length,
        });
        if (error) throw error;
        await load();
    }, [companyId, userId, replies.length, load]);

    const update = useCallback(async (id: number, input: Pick<QuickReplyInput, 'title' | 'body'>) => {
        if (!supabase) return;
        const { error } = await supabase.from('quick_replies')
            .update({ title: input.title.trim(), body: input.body.trim(), updated_at: new Date().toISOString() })
            .eq('id', id);
        if (error) throw error;
        await load();
    }, [load]);

    const remove = useCallback(async (id: number) => {
        if (!supabase) return;
        const { error } = await supabase.from('quick_replies').delete().eq('id', id);
        if (error) throw error;
        setReplies(prev => prev.filter(r => r.id !== id));
    }, []);

    return { replies, loading, create, update, remove, reload: load };
}
